import React from "react";
import styled from "styled-components";
import { ConfigContext } from "@/contexts/appContexts";
import { Wrapper, Input } from "./styled";
import { TagElement, WebComponents } from "@/interfaces";
import { Badge } from "@/components/ui";
import { XIcon } from "lucide-react";
import { useAccountSettings, validateEmail } from "@/utils";

interface Props {
  tags: TagElement[];

  placeholder?: string;
  dataElmId: string;
  tagDeleteIcon?: string;
  addTagOnEnterKeyPressed?: boolean;
  uniquenessTags?: boolean;

  tagDeleteStyle?: string;
  tagStyle?: string;
  inputStyle?: string;
  wrapperStyle?: string;

  onTagsChanged: (tags: any[]) => void;
  onInputChanged?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onInputError: (errors: string[]) => void;
}

export const EmailTag: React.FC<Props> = ({
  tags,
  uniquenessTags = true,
  dataElmId,
  placeholder = "Type some Email...",
  addTagOnEnterKeyPressed = true,
  inputStyle,
  wrapperStyle,
  onTagsChanged,
  onInputChanged,
  onInputError,
}) => {
  const [selectedTags, setSelectedTags] = React.useState(tags);

  const input = React.useRef<HTMLInputElement | null>(null);
  const { keywordMaxLength } = React.useContext(ConfigContext);
  const { hasWritePermission } = useAccountSettings();
  const withWritePermission = hasWritePermission(WebComponents.DIGEST_EMAIL);

  React.useEffect(() => {
    setSelectedTags(() => tags);
  }, [tags]);

  React.useEffect(() => {
    clearInput();
    onTagsChanged(selectedTags);
  }, [selectedTags]);

  const addTag = (displayValue: string) => {
    setSelectedTags(selected => {
      return selected.concat([
        {
          index: selected.length + 1,
          displayValue: displayValue.trim(),
        },
      ]);
    });
  };
  const invalidEmailMessage = (email: string): string => {
    return `${email} is an invalid email.`;
  };
  const duplicatedEmailMessage = (email: string): string => {
    return `The email ${email} is already being used.`;
  };
  const getInvalidError = (validEmail: boolean, email: string): string => {
    return !validEmail ? invalidEmailMessage(email) : duplicatedEmailMessage(email);
  };

  const onInputKeyUp = (e: any): void => {
    if (withWritePermission) {
      const inputValue = e.target.value ? e.target.value.trim() : "";
      const inputNotEmpty = inputValue !== "";
      if (e.key === "Enter" && inputNotEmpty && addTagOnEnterKeyPressed && validate(inputValue)) {
        addTag(inputValue);
        onInputError([]);
      }
    }
  };

  const onInputPaste = (pasteValue: string): void => {
    if (withWritePermission) {
      const inputValues = Array.from(new Set(pasteValue.split(",")));

      const errors: string[] = [];
      inputValues.map(value => {
        const inputValue = value.trim();
        const isValid = validateBulk(inputValue);
        const validEmail = validateEmail(inputValue);
        const inputNotEmpty = inputValue !== "";
        if (inputNotEmpty && addTagOnEnterKeyPressed && isValid && validEmail) {
          addTag(inputValue);
        } else if (!isValid || !validEmail) {
          errors.push(getInvalidError(validEmail, inputValue));
        }
      });

      if (errors.length > 0) {
        onInputError(errors);
      }
    }
  };

  const inputWillChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    if (onInputChanged && withWritePermission) {
      onInputChanged(e);
    }
  };

  const onInputKeyDown = (e: any): void => {
    if (withWritePermission) {
      const deleteLastTag = () => {
        setSelectedTags(oldTags => {
          return [...oldTags.splice(0, oldTags.length - 1)];
        });
      };

      if (e.key === "Backspace") {
        onInputError([]);
      }
      if (e.key === "Backspace" && e.target.selectionStart === 0) {
        deleteLastTag();
      } else if (e.key !== "Backspace" && e.target.value.length >= keywordMaxLength) {
        e.preventDefault();
      }
    }
  };

  const validate = (newValue: string): boolean => {
    if (!validateEmail(newValue)) {
      onInputError([invalidEmailMessage(newValue)]);
      return false;
    }

    if (!uniquenessTags) {
      return true;
    }

    if (selectedTags.map(t => t.displayValue.toLowerCase()).includes(newValue.toLowerCase())) {
      onInputError([duplicatedEmailMessage(newValue)]);
      return false;
    }

    return true;
  };

  const validateBulk = (newValue: string): boolean => {
    if (!uniquenessTags) {
      return true;
    }

    if (selectedTags.map(t => t.displayValue.toLowerCase()).includes(newValue.toLowerCase())) {
      return false;
    }

    return true;
  };

  const clearInput = (): void => {
    input!.current!.value = "";
  };
  const focusInput = (): void => {
    input!.current!.focus();
  };

  const removeTag = (index: number): void => {
    if (withWritePermission) {
      onInputError([]);
      setSelectedTags(oldTags => {
        return [...oldTags.filter(tag => tag.index !== index)];
      });
    }
  };

  const renderTags = (): any => {
    return selectedTags.length == 0 ? null : (
      <div className="flex flex-wrap gap-2">
        {selectedTags.map((tag, index) => (
          <Badge key={index} variant="outline" className={"text-sm"}>
            {tag && tag.displayValue}
            <XIcon size={13} onClick={() => removeTag(tag.index)} className="ml-2 cursor-pointer" />
          </Badge>
        ))}
      </div>
    );
  };

  const renderPlaceholder = (): string | undefined => {
    return selectedTags && selectedTags.length > 0 ? undefined : placeholder;
  };

  const getInputWrapperStyledComponent = () => {
    return wrapperStyle
      ? styled(Wrapper)`
          ${wrapperStyle}
        `
      : Wrapper;
  };

  const getInputStyledComponent = () => {
    return inputStyle
      ? styled(Input)`
          ${inputStyle}
        `
      : Input;
  };

  const InputWrapper = getInputWrapperStyledComponent();
  const InputComponent = getInputStyledComponent();
  return (
    <InputWrapper onClick={focusInput}>
      {renderTags()}
      <InputComponent
        ref={input}
        data-elm-id={dataElmId}
        onChange={inputWillChange}
        placeholder={renderPlaceholder()}
        type="text"
        onKeyUp={onInputKeyUp}
        onKeyDown={onInputKeyDown}
        disabled={!withWritePermission}
        onPaste={text => {
          onInputPaste(text.clipboardData.getData("Text"));
          text.preventDefault();
        }}
      />
    </InputWrapper>
  );
};
